import React, { useEffect, useRef } from 'react';
import { ConnectionType } from '../types';
import { CONNECTION_TYPE_CONFIG } from '../constants';
import { ArrowRightLeft, Repeat, Trash2, X } from 'lucide-react';

interface ConnectionQuickMenuProps {
  x: number;
  y: number;
  connectionId: string;
  connectionType: ConnectionType;
  isBidirectional: boolean;
  onTypeChange: (type: ConnectionType) => void;
  onToggleBidirectional: () => void;
  onDelete: () => void;
  onDismiss: () => void;
}

const ConnectionQuickMenu: React.FC<ConnectionQuickMenuProps> = ({
  x,
  y,
  connectionId,
  connectionType,
  isBidirectional,
  onTypeChange,
  onToggleBidirectional,
  onDelete,
  onDismiss
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  // Auto-dismiss after 3 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss();
    }, 3000);

    return () => clearTimeout(timer);
  }, [onDismiss]);

  // Common connection types for quick selection
  const commonTypes: ConnectionType[] = [
    ConnectionType.HTTP,
    ConnectionType.HTTPS,
    ConnectionType.TCP,
    ConnectionType.GRPC,
    ConnectionType.JDBC
  ];

  return (
    <div
      ref={menuRef}
      style={{ 
        position: 'fixed',
        left: '50%',
        bottom: '20px',
        transform: 'translateX(-50%)',
        zIndex: 110,
        animationDuration: '300ms',
        animationTimingFunction: 'cubic-bezier(0.34, 1.56, 0.64, 1)'
      }}
      className="bg-white border border-gray-200 rounded-lg shadow-xl py-2 min-w-[200px] animate-slide-in-up will-change-transform"
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        e.preventDefault();
      }}
    >
      <div className="px-3 py-2 text-xs font-bold text-gray-500 uppercase tracking-wider border-b border-gray-100 mb-1 flex items-center justify-between">
        <span>Quick Actions</span>
        <button
          onClick={onDismiss}
          className="text-gray-400 hover:text-black transition-colors"
        >
          <X size={14} />
        </button>
      </div>

      {/* Quick type selection */}
      <div className="px-3 py-1.5 text-xs font-bold text-gray-500 uppercase tracking-wider">
        Change Type
      </div>
      {commonTypes.map((type) => {
        const config = CONNECTION_TYPE_CONFIG[type];
        return (
          <button
            key={type}
            onClick={() => {
              onTypeChange(type);
              onDismiss();
            }}
            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-all duration-150 active:scale-95 ${
              connectionType === type 
                ? 'bg-blue-50 text-blue-700 font-medium' 
                : 'text-gray-700 hover:bg-gray-50'
            }`}
            title={config?.description}
          >
            <span className="flex items-center gap-2">
              {config?.icon && <span style={{ color: config.color }}>{config.icon}</span>}
              {type}
            </span>
          </button>
        );
      })}

      <div className="my-1 border-t border-gray-100"></div>

      {/* Bidirectional toggle */}
      <button
        onClick={() => {
          onToggleBidirectional();
          onDismiss();
        }}
        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-all duration-150 active:scale-95"
      >
        <Repeat size={14} />
        {isBidirectional ? 'Make Unidirectional' : 'Make Bidirectional'}
      </button>

      <div className="my-1 border-t border-gray-100"></div>

      {/* Delete */}
      <button
        onClick={() => {
          onDelete();
          onDismiss();
        }}
        className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-all duration-150 active:scale-95"
      >
        <Trash2 size={14} />
        Delete Connection
      </button>
    </div>
  );
};

export default ConnectionQuickMenu;
